import { UNLIMITED_AI_CREDITS_SENTINEL } from "@hex/common";
import React from "react";
import { styled } from "styled-components";

import { Flex, HexProgressBar, Txt } from "../../hex-components";

import {
  formatCreditResetMonthAndOrdinalDay,
  formatCreditsForDisplay,
  isBeforeAiBillingEnforcementDate,
} from "./utils";

type BasicCreditMeterProps = {
  usedCredits: number;
  totalCredits: number | typeof UNLIMITED_AI_CREDITS_SENTINEL;
  resetDate: Date;
  remainingCreditsIcon?: React.ReactNode;
  enforcementDate?: Date | null;
};

export const BasicCreditMeter = React.memo(function BasicCreditMeter({
  enforcementDate,
  remainingCreditsIcon,
  resetDate,
  totalCredits,
  usedCredits,
}: BasicCreditMeterProps) {
  const hasUnlimitedCredits = totalCredits === UNLIMITED_AI_CREDITS_SENTINEL;

  // When set and in the future, all usage debits the user pool - including overflow
  // past the monthly cap - so usedCredits can exceed totalCredits. We want to show this
  // as a non-blocking overage state
  const isPreEnforcement = isBeforeAiBillingEnforcementDate(enforcementDate);
  const isOverCap =
    isPreEnforcement &&
    !hasUnlimitedCredits &&
    totalCredits > 0 &&
    usedCredits > totalCredits;
  const overageAmount = isOverCap ? usedCredits - totalCredits : 0;

  const remainingCreditsPercentage =
    totalCredits === 0
      ? 0
      : hasUnlimitedCredits
        ? 1
        : 1 - usedCredits / totalCredits;

  const remainingCreditsString = hasUnlimitedCredits
    ? "Unlimited remaining"
    : `${formatCreditsForDisplay(Math.max(0, totalCredits - usedCredits))}/${formatCreditsForDisplay(totalCredits)} remaining`;

  const resetDateString = formatCreditResetMonthAndOrdinalDay(resetDate);

  return (
    <Flex align="stretch" css="width: 100%" direction="column" gap={10}>
      <Flex direction="row" justify="between">
        <Txt>Your credits</Txt>
        <Txt fontColor="muted" fontSize="small">
          Resets {resetDateString}
        </Txt>
      </Flex>
      <HexProgressBar
        $small={true}
        intent="primary"
        value={remainingCreditsPercentage}
      />
      <Flex align="center" direction="row">
        {remainingCreditsIcon && (
          <StyledRemainingCreditsIconContainer>
            {remainingCreditsIcon}
          </StyledRemainingCreditsIconContainer>
        )}
        <Txt fontColor="muted" fontSize="small">
          {remainingCreditsString}
        </Txt>
      </Flex>
      {isOverCap && (
        <Txt fontColor="muted" fontSize="small">
          {`+${formatCreditsForDisplay(overageAmount)} over your monthly cap`}
        </Txt>
      )}
    </Flex>
  );
});

const StyledRemainingCreditsIconContainer = styled.div`
  padding-right: 5px;
`;
